import { ChevronLeft, ChevronRight } from 'lucide-react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface PaginationProps {
  currentPage: number
  totalPages: number
  totalCount: number
  perPage: number
  onPageChange: (page: number) => void
  onPerPageChange?: (perPage: number) => void
  showPerPageSelector?: boolean
  className?: string
  compact?: boolean
}

export function Pagination({
  currentPage,
  totalPages,
  totalCount,
  perPage,
  onPageChange,
  onPerPageChange,
  showPerPageSelector = true,
  className = '',
  compact = false,
}: PaginationProps) {
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1)
    }
  }

  const handlePageClick = (page: number) => {
    onPageChange(page)
  }

  const handlePerPageChange = (value: string) => {
    const newPerPage = parseInt(value)
    onPerPageChange?.(newPerPage)
  }

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = []

    if (compact) {
      // Compact mode: maximum 5 elements, smart transitions

      if (totalPages <= 5) {
        // Show all pages if we have 5 or fewer
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        if (currentPage <= 2) {
          // At the very beginning: 1 2 3 ... last
          pages.push(1, 2, 3, 'ellipsis', totalPages)
        } else if (currentPage >= totalPages - 1) {
          // At the very end: 1 ... (last-2) (last-1) last
          pages.push(1, 'ellipsis', totalPages - 2, totalPages - 1, totalPages)
        } else {
          // In the middle: 1 ... current ... last (only 5 elements)
          pages.push(1, 'ellipsis', currentPage, 'ellipsis', totalPages)
        }
      }
    } else {
      // Standard pagination with smart truncation
      const maxVisible = 5

      if (totalPages <= maxVisible) {
        // Show all pages if we have few enough
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        // Always show first page
        pages.push(1)

        // Determine range around current page
        const start = Math.max(2, currentPage - 1)
        const end = Math.min(totalPages - 1, currentPage + 1)

        // Add ellipsis after first page if there's a gap
        if (start > 2) {
          pages.push('ellipsis')
        }

        // Add pages around current page
        for (let i = start; i <= end; i++) {
          pages.push(i)
        }

        // Add ellipsis before last page if there's a gap
        if (end < totalPages - 1) {
          pages.push('ellipsis')
        }

        // Always show last page
        if (totalPages > 1) {
          pages.push(totalPages)
        }
      }
    }

    return pages
  }

  const startItem = (currentPage - 1) * perPage + 1
  const endItem = Math.min(currentPage * perPage, totalCount)

  return (
    <div className={`flex items-center justify-between ${className}`}>
      {/* Results info */}
      <div className="flex items-center gap-4">
        <p className="text-sm text-foreground">
          Showing {startItem} to {endItem} of {totalCount} results
        </p>

        {/* Per page selector */}
        {showPerPageSelector && onPerPageChange && (
          <div className="flex items-center gap-2">
            <label className="text-sm text-foreground">Show:</label>
            <Select
              value={perPage.toString()}
              onValueChange={handlePerPageChange}
            >
              <SelectTrigger className="w-[80px] text-sm h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Page navigation */}
      <div className="flex items-center gap-1">
        {/* Previous button */}
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          className="p-2 rounded-md border border-border bg-background hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {/* Page numbers */}
        {getPageNumbers().map((page, index) => {
          if (page === 'ellipsis') {
            return (
              <span
                key={`ellipsis-${index}`}
                className="px-3 py-2 text-sm text-slate-400"
              >
                ...
              </span>
            )
          }

          return (
            <button
              key={page}
              onClick={() => handlePageClick(page)}
              className={`px-3 py-2 text-sm rounded-md border ${
                page === currentPage
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-foreground border-border hover:bg-accent'
              }`}
            >
              {page}
            </button>
          )
        })}

        {/* Next button */}
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className="p-2 rounded-md border border-border bg-background hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
